import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import type { AdminOrderResumo, AdminOrdersResponse, OrderKind, OrderStatus } from '../api/types';
import {
  NOME_DO_TIPO,
  brl,
  linkWhatsapp,
  proximoPasso,
  recadoParaCliente,
  rotuloCurto,
  tempoDesde,
  valorDoPedido,
} from '../lib/pedidos';
import { PedidoDetalhe } from './pedidos/PedidoDetalhe';
import { ConfigPedidos } from './pedidos/ConfigPedidos';

/** De quanto em quanto tempo a fila relê sozinha. */
const RELEITURA_MS = 30_000;

/** As colunas do quadro, na ordem do trabalho. */
const COLUNAS: { status: OrderStatus; titulo: string; dica: string }[] = [
  { status: 'enviado', titulo: 'Aguardando compra', dica: 'O cliente mandou o pedido. Falta comprar.' },
  { status: 'processando', titulo: 'Compra realizada', dica: 'Comprado. Falta enviar a proposta de troca.' },
  { status: 'concluido', titulo: 'Finalizados', dica: 'Entregues.' },
];

const PERIODOS: { dias: number | undefined; rotulo: string }[] = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
  { dias: undefined, rotulo: 'Tudo' },
];

type Visao = 'quadro' | 'lista';

/**
 * Gestão de pedidos.
 *
 * O painel responde, nesta ordem, as perguntas de quem abre a fila: o que está
 * esperando por mim, o que está atrasado, e como foi o mês. O quadro mostra o
 * pedido andando pelas três etapas; a lista serve para procurar e para ver
 * cancelados e não enviados. O detalhe abre numa gaveta ao lado, sem tirar a
 * fila da tela.
 *
 * A fila relê sozinha a cada 30 segundos: pedido novo que chega do WhatsApp
 * aparece sem ninguém recarregar.
 */
export function OrdersPanel() {
  const [data, setData] = useState<AdminOrdersResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [visao, setVisao] = useState<Visao>('quadro');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [tipo, setTipo] = useState<OrderKind | 'todos'>('todos');
  const [dias, setDias] = useState<number | undefined>(30);
  const [statusLista, setStatusLista] = useState<OrderStatus | 'todos'>('todos');
  const [aberto, setAberto] = useState<AdminOrderResumo | null>(null);
  const [configAberta, setConfigAberta] = useState(false);
  const [movendo, setMovendo] = useState<string | null>(null);
  /** O último pedido movido pelo card, para oferecer o aviso ao cliente. */
  const [movido, setMovido] = useState<{ pedido: AdminOrderResumo; status: OrderStatus } | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<number>(Date.now());

  // Busca espera a digitação parar.
  useEffect(() => {
    const t = window.setTimeout(() => setBuscaAplicada(busca.trim()), 350);
    return () => window.clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    try {
      const res = await api.adminPedidos({
        // O quadro precisa de todas as etapas de uma vez; o filtro de status
        // é da lista.
        status: visao === 'lista' ? statusLista : 'todos',
        kind: tipo,
        search: buscaAplicada || undefined,
        dias,
        limit: 300,
      });
      setData(res);
      setErro(null);
      setAtualizadoEm(Date.now());
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os pedidos.');
    }
  }, [visao, statusLista, tipo, buscaAplicada, dias]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar();
    }, RELEITURA_MS);
    return () => window.clearInterval(t);
  }, [carregar]);

  const fecharDetalhe = useCallback(() => setAberto(null), []);

  /** Avança o pedido direto do card, sem abrir o detalhe. */
  async function avancar(o: AdminOrderResumo) {
    const passo = proximoPasso(o.kind, o.status);
    if (!passo) return;
    setMovendo(o.code);
    setErro(null);
    try {
      await api.adminMudarStatusPedido(o.code, passo.status);
      setMovido({ pedido: o, status: passo.status });
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui mudar a etapa.');
    } finally {
      setMovendo(null);
    }
  }

  const porStatus = useMemo(() => {
    const mapa = new Map<OrderStatus, AdminOrderResumo[]>();
    for (const o of data?.items ?? []) {
      const lista = mapa.get(o.status) ?? [];
      lista.push(o);
      mapa.set(o.status, lista);
    }
    // Na fila, o mais antigo primeiro: é o que espera há mais tempo.
    for (const [status, lista] of mapa) {
      if (status !== 'concluido' && status !== 'cancelado') {
        lista.sort((a, b) => new Date(a.statusDesde).getTime() - new Date(b.statusDesde).getTime());
      }
    }
    return mapa;
  }, [data]);

  if (erro && !data) return <Banner kind="error">{erro}</Banner>;
  if (!data) return <Spinner label="Carregando pedidos…" />;

  const total = (s: string) => data.totais.find((t) => t.status === s)?.quantidade ?? 0;
  const soma = (s: string) => data.totais.find((t) => t.status === s)?.somaBrl ?? 0;
  const lucro = (s: string) => data.totais.find((t) => t.status === s)?.lucroBrl ?? 0;
  const atrasado = (o: AdminOrderResumo) => {
    const limite = data.limiteHoras[o.status as keyof typeof data.limiteHoras];
    return limite !== undefined && Date.now() - new Date(o.statusDesde).getTime() > limite * 3_600_000;
  };
  const naoEnviados = porStatus.get('aberto') ?? [];

  return (
    <div className="panel gp">
      <header className="gp-topo">
        <div>
          <h2>Pedidos</h2>
          <p className="muted">
            Atualizado {tempoDesde(new Date(atualizadoEm).toISOString())} · relê sozinho a cada 30 s
          </p>
        </div>
        <div className="actions-row">
          <button className="btn ghost small" onClick={() => void carregar()}>
            Atualizar agora
          </button>
          <button
            className={`btn ghost small ${configAberta ? 'ativo' : ''}`}
            onClick={() => setConfigAberta((v) => !v)}
            aria-expanded={configAberta}
          >
            Configurações
          </button>
        </div>
      </header>

      {configAberta && <ConfigPedidos whatsappAtual={data.whatsapp} onSalvo={() => void carregar()} />}
      {!data.whatsapp && (
        <Banner kind="warn">
          O WhatsApp do atendimento não está configurado: o fechamento da sacola não abre conversa nenhuma. Abra
          Configurações e informe o número.
        </Banner>
      )}
      {erro && <Banner kind="error">{erro}</Banner>}

      {movido && (
        <MovidoAviso movido={movido} onDispensar={() => setMovido(null)} />
      )}

      {/* ── Números da operação ─────────────────────────────────────────── */}
      <div className="gp-kpis">
        <div className={`gp-kpi ${data.atrasados.enviado ? 'alerta' : ''}`}>
          <span>Aguardando compra</span>
          <b>{total('enviado')}</b>
          <small>
            {brl(soma('enviado'))}
            {data.atrasados.enviado ? ` · ${data.atrasados.enviado} há mais de ${data.limiteHoras.enviado} h` : ''}
          </small>
        </div>
        <div className={`gp-kpi ${data.atrasados.processando ? 'alerta' : ''}`}>
          <span>Compra realizada</span>
          <b>{total('processando')}</b>
          <small>
            troca a enviar
            {data.atrasados.processando
              ? ` · ${data.atrasados.processando} há mais de ${data.limiteHoras.processando} h`
              : ''}
          </small>
        </div>
        <div className="gp-kpi">
          <span>Finalizados no mês</span>
          <b>{data.mes.concluidos}</b>
          <small>
            receita {brl(data.mes.receitaBrl)}
            {data.mes.concluidos > 0 ? ` · ${data.mes.horasAteConcluir.toFixed(1)} h em média` : ''}
          </small>
        </div>
        <div className="gp-kpi destaque">
          <span>Lucro do mês</span>
          <b>{brl(data.mes.lucroBrl)}</b>
          <small>+ {brl(lucro('enviado') + lucro('processando'))} em andamento</small>
        </div>
      </div>

      {naoEnviados.length > 0 && visao === 'quadro' && (
        <Banner kind="warn">
          {naoEnviados.length === 1
            ? '1 cliente fechou o pedido e não chegou ao WhatsApp.'
            : `${naoEnviados.length} clientes fecharam o pedido e não chegaram ao WhatsApp.`}{' '}
          Veja em Lista → Não enviados e chame pelo WhatsApp deles.
        </Banner>
      )}

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <div className="gp-filtros">
        <div className="gp-segmento" role="tablist" aria-label="Visualização">
          {(['quadro', 'lista'] as Visao[]).map((v) => (
            <button key={v} role="tab" aria-selected={visao === v} className={visao === v ? 'ativo' : ''} onClick={() => setVisao(v)}>
              {v === 'quadro' ? 'Quadro' : 'Lista'}
            </button>
          ))}
        </div>
        <input
          id="gp-busca"
          className="input gp-busca"
          placeholder="Buscar por código, cliente ou SteamID"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select id="gp-tipo" className="select" value={tipo} onChange={(e) => setTipo(e.target.value as OrderKind | 'todos')}>
          <option value="todos">Todos os tipos</option>
          <option value="encomenda">Encomenda</option>
          <option value="upgrade">Upgrade</option>
          <option value="venda">Venda de inventário</option>
        </select>
        <select
          id="gp-periodo"
          className="select"
          value={dias ?? ''}
          onChange={(e) => setDias(e.target.value ? Number(e.target.value) : undefined)}
        >
          {PERIODOS.map((p) => (
            <option key={p.rotulo} value={p.dias ?? ''}>
              {p.rotulo}
            </option>
          ))}
        </select>
      </div>

      {/* ── Quadro ──────────────────────────────────────────────────────── */}
      {visao === 'quadro' && (
        <div className="gp-quadro">
          {COLUNAS.map((c) => {
            const lista = porStatus.get(c.status) ?? [];
            return (
              <section key={c.status} className={`gp-coluna ${c.status}`} aria-label={c.titulo}>
                <header>
                  <b>{c.titulo}</b>
                  <span className="gp-contagem">{lista.length}</span>
                </header>
                <p className="muted gp-dica">{c.dica}</p>
                {lista.length === 0 ? (
                  <p className="gp-vazio">Nenhum pedido aqui.</p>
                ) : (
                  <ul className="gp-cards">
                    {lista.map((o) => (
                      <li key={o.code}>
                        <CartaoPedido
                          o={o}
                          atrasado={atrasado(o)}
                          movendo={movendo === o.code}
                          onAbrir={() => setAberto(o)}
                          onAvancar={() => void avancar(o)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* ── Lista ───────────────────────────────────────────────────────── */}
      {visao === 'lista' && (
        <>
          <div className="gp-chips" role="tablist" aria-label="Etapa">
            {(
              [
                ['todos', 'Todos'],
                ['enviado', 'Aguardando compra'],
                ['processando', 'Compra realizada'],
                ['concluido', 'Finalizados'],
                ['aberto', 'Não enviados'],
                ['cancelado', 'Cancelados'],
              ] as [OrderStatus | 'todos', string][]
            ).map(([s, rotulo]) => (
              <button
                key={s}
                role="tab"
                aria-selected={statusLista === s}
                className={statusLista === s ? 'ativo' : ''}
                onClick={() => setStatusLista(s)}
              >
                {rotulo}
                {s !== 'todos' && <span>{total(s)}</span>}
              </button>
            ))}
          </div>

          <div className="table-wrap">
            <table className="admin-table gp-tabela">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Etapa</th>
                  <th>Parado</th>
                  <th>Valor</th>
                  <th>Lucro</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => {
                  const passo = proximoPasso(o.kind, o.status);
                  return (
                    <tr key={o.code} className={aberto?.code === o.code ? 'selecionada' : ''}>
                      <td>
                        <button className="gp-link" onClick={() => setAberto(o)}>
                          {o.code}
                        </button>
                        <div className="dim">
                          {o.itens} {o.itens === 1 ? 'item' : 'itens'}
                        </div>
                      </td>
                      <td>{o.personaName ?? '—'}</td>
                      <td>{NOME_DO_TIPO[o.kind]}</td>
                      <td>
                        <span className={`gp-selo ${o.status}`}>{rotuloCurto(o.kind, o.status)}</span>
                      </td>
                      <td className={atrasado(o) ? 'gp-atraso' : 'dim'}>
                        {o.status === 'concluido' || o.status === 'cancelado' ? '—' : tempoDesde(o.statusDesde)}
                      </td>
                      <td>
                        <b>{brl(valorDoPedido(o))}</b>
                      </td>
                      <td>{o.profitBrl === null ? <span className="dim">—</span> : <span className="pos">{brl(o.profitBrl)}</span>}</td>
                      <td>
                        {passo ? (
                          <button className="btn ghost small" onClick={() => void avancar(o)} disabled={movendo === o.code}>
                            {passo.acao}
                          </button>
                        ) : (
                          <button className="btn ghost small" onClick={() => setAberto(o)}>
                            Abrir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.items.length === 0 && <p className="muted">Nenhum pedido com esses filtros.</p>}
        </>
      )}

      {aberto && (
        <PedidoDetalhe
          code={aberto.code}
          steamId={aberto.steamId}
          onFechar={fecharDetalhe}
          onMudou={() => void carregar()}
        />
      )}
    </div>
  );
}

/** Um pedido no quadro: o que se precisa ver para decidir sem abrir o detalhe. */
function CartaoPedido({
  o,
  atrasado,
  movendo,
  onAbrir,
  onAvancar,
}: {
  o: AdminOrderResumo;
  atrasado: boolean;
  movendo: boolean;
  onAbrir: () => void;
  onAvancar: () => void;
}) {
  const passo = proximoPasso(o.kind, o.status);
  return (
    <article className={`gp-card ${atrasado ? 'atrasado' : ''}`}>
      <button className="gp-card-corpo" onClick={onAbrir} aria-label={`Abrir o pedido ${o.code}`}>
        {o.capaIconUrl ? <img src={o.capaIconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
        <div className="gp-card-info">
          <div className="gp-card-linha">
            <b>{o.code}</b>
            {o.kind !== 'encomenda' && <span className={`gp-tipo ${o.kind}`}>{NOME_DO_TIPO[o.kind]}</span>}
          </div>
          <span className="gp-card-cliente">{o.personaName ?? 'Cliente sem nome'}</span>
          <span className="muted gp-card-itens">
            {o.capaNome ?? ''}
            {o.itens > 1 ? ` +${o.itens - 1}` : ''}
          </span>
          <div className="gp-card-linha">
            <b>{brl(valorDoPedido(o))}</b>
            {o.profitBrl !== null && <span className="pos">+{brl(o.profitBrl)}</span>}
          </div>
        </div>
      </button>
      <footer className="gp-card-rodape">
        <span className={atrasado ? 'gp-atraso' : 'muted'}>
          {o.status === 'concluido' ? 'finalizado ' : ''}
          {tempoDesde(o.statusDesde)}
        </span>
        {passo && (
          <button className="btn primary small" onClick={onAvancar} disabled={movendo}>
            {movendo ? 'Movendo…' : passo.acao}
          </button>
        )}
      </footer>
    </article>
  );
}

/** Depois de mover pelo card, o atalho para avisar o cliente. */
function MovidoAviso({
  movido,
  onDispensar,
}: {
  movido: { pedido: AdminOrderResumo; status: OrderStatus };
  onDispensar: () => void;
}) {
  const link = linkWhatsapp(movido.pedido.whatsapp, recadoParaCliente({ ...movido.pedido, status: movido.status }));
  return (
    <div className="gp-aviso-cliente">
      <span>
        <b>{movido.pedido.code}</b> movido para <b>{rotuloCurto(movido.pedido.kind, movido.status)}</b>.
        {link ? ' Avise o cliente pelo WhatsApp dele:' : ' O cliente não informou um WhatsApp válido.'}
      </span>
      <div className="actions-row">
        {link && (
          <a className="btn primary small" href={link} target="_blank" rel="noreferrer noopener" onClick={onDispensar}>
            Avisar o cliente
          </a>
        )}
        <button className="btn ghost small" onClick={onDispensar}>
          Dispensar
        </button>
      </div>
    </div>
  );
}
